import React, { useMemo, useState } from 'react';
import { AtSign, CheckCheck, CheckCircle2, RefreshCw } from 'lucide-react';
import { AppNotification, isUnread } from '../../services/notificationStore';
import { relativeTime, unreadFirst } from './notificationFormat';
import { EmptyState, LinkButton, SkeletonRows } from '../dashboard/dashboardUi';

/**
 * `P` ist die Zeile der Projektliste des Aufrufers (Dashboard: DashboardProject);
 * die Liste selbst liest nur `id` und reicht die gefundene Zeile unveraendert
 * an `onOpenProject` zurueck.
 */
interface Props<P extends { id: string }> {
    items: ReadonlyArray<AppNotification>;
    loading: boolean;
    error: boolean;
    /** Geladene Projektliste — der Projektname in der Zeile oeffnet das Projekt daraus. */
    projects: ReadonlyArray<P>;
    onOpenProject: (project: P) => void;
    onMarkRead: (n: AppNotification) => Promise<void> | void;
    onMarkAllRead: () => Promise<void> | void;
    onRetry?: () => void;
    nowMs: number;
    /** Nur die ersten N zeigen; darunter „alle anzeigen" (`onShowAll`). */
    limit?: number;
    onShowAll?: () => void;
    /** Enge Spalte: Inhalt auf zwei Zeilen kuerzen. */
    compact?: boolean;
    className?: string;
}

/**
 * Die Mitteilungsliste — ungelesene zuerst, je Zeile Absender, Projekt,
 * Zeitabstand, Inhalt und „Gelesen". Dieselbe Komponente sitzt in der
 * Dashboard-Spalte, im Handy-Blatt und koennte im Aufgaben-Reiter sitzen.
 */
export const NotificationList = <P extends { id: string }>({
    items, loading, error, projects, onOpenProject, onMarkRead, onMarkAllRead, onRetry,
    nowMs, limit, onShowAll, compact = false, className = '',
}: Props<P>): React.ReactElement => {
    const [busyId, setBusyId] = useState<string | null>(null);
    const [allBusy, setAllBusy] = useState(false);

    const sorted = useMemo(() => unreadFirst(items), [items]);
    const shown = limit ? sorted.slice(0, limit) : sorted;
    const unread = useMemo(() => items.filter(isUnread).length, [items]);
    const hidden = sorted.length - shown.length;

    const markOne = async (n: AppNotification) => {
        setBusyId(n.id);
        try { await onMarkRead(n); } catch { /* der Speicher laedt bei Fehlern neu; hier nichts zu tun */ } finally { setBusyId(null); }
    };
    const markAll = async () => {
        setAllBusy(true);
        try { await onMarkAllRead(); } catch { /* s. o. */ } finally { setAllBusy(false); }
    };

    if (loading && items.length === 0) return <SkeletonRows rows={compact ? 3 : 5} className={className} />;

    if (error && items.length === 0) {
        return (
            <div className={`px-4 py-6 text-center ${className}`}>
                <p className="text-xs font-semibold text-red-700">Mitteilungen konnten nicht geladen werden.</p>
                {onRetry && (
                    <button type="button" onClick={onRetry} className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 hover:underline underline-offset-2">
                        <RefreshCw className="w-3 h-3" /> Erneut versuchen
                    </button>
                )}
            </div>
        );
    }

    if (items.length === 0) {
        return <EmptyState tone="neutral" icon={<AtSign />} title="Keine Mitteilungen" hint="Erwähnungen aus Projektnotizen und Freigaben erscheinen hier." className={className} />;
    }

    return (
        <div className={`flex flex-col min-h-0 ${className}`}>
            <div className="px-3.5 py-1.5 flex items-center justify-between gap-2 border-b border-slate-100">
                <span className="text-[11px] text-slate-500">
                    {unread > 0 ? <><span className="font-semibold text-slate-700 tabular">{unread}</span> ungelesen</> : 'Alles gelesen'}
                </span>
                {unread > 0 && (
                    <LinkButton onClick={markAll} disabled={allBusy} className="inline-flex items-center gap-1">
                        <CheckCheck className="w-3 h-3" /> Alle gelesen
                    </LinkButton>
                )}
            </div>
            <ul className="divide-y divide-slate-100">
                {shown.map(n => {
                    const offen = isUnread(n);
                    const project = n.project_id ? projects.find(p => p.id === n.project_id) : undefined;
                    return (
                        <li key={n.id} className={`px-3.5 py-2.5 ${offen ? 'bg-white' : 'bg-slate-50/60'}`}>
                            <div className="flex items-start gap-2">
                                <span aria-hidden="true" className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${offen ? 'bg-amber-500' : 'bg-transparent'}`} />
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-baseline justify-between gap-2">
                                        <span className={`text-[11px] truncate ${offen ? 'font-semibold text-slate-800' : 'text-slate-500'}`}>{n.sender_name || 'Unbekannt'}</span>
                                        <time dateTime={n.created_at} className="text-[11px] text-slate-400 shrink-0 tabular">{relativeTime(n.created_at, nowMs)}</time>
                                    </div>
                                    <p className={`text-xs mt-0.5 break-words ${compact ? 'line-clamp-2' : ''} ${offen ? 'text-slate-700' : 'text-slate-500'}`}>{n.content}</p>
                                    <div className="flex items-center justify-between gap-2 mt-1">
                                        {n.project_name ? (
                                            project ? (
                                                <button type="button" onClick={() => onOpenProject(project)} className="text-[11px] font-semibold text-blue-700 hover:underline underline-offset-2 truncate text-left">
                                                    {n.project_name}
                                                </button>
                                            ) : (
                                                <span className="text-[11px] text-slate-400 truncate" title="Projekt nicht in deiner Liste">{n.project_name}</span>
                                            )
                                        ) : <span />}
                                        {offen ? (
                                            <button
                                                type="button"
                                                onClick={() => markOne(n)}
                                                disabled={busyId === n.id}
                                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 hover:text-emerald-700 px-1.5 py-1 -my-1 rounded-md hover:bg-emerald-50 disabled:opacity-50 shrink-0"
                                            >
                                                <CheckCircle2 className="w-3 h-3" /> Gelesen
                                            </button>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-[11px] text-slate-400 shrink-0"><CheckCircle2 className="w-3 h-3" /> Gelesen</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ul>
            {hidden > 0 && onShowAll && (
                <div className="px-3.5 py-2 border-t border-slate-100 text-center">
                    <LinkButton onClick={onShowAll}>Alle {sorted.length} anzeigen</LinkButton>
                </div>
            )}
        </div>
    );
};
